import { useEffect, useState } from "react";
import api, { fmt, fmtCents } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Receipt } from "lucide-react";

/** Parse a dollar string like "$1,234.56" or "$45" to a number. */
export function parseDollar(s) {
  return Number((s || "").replace(/[$,\s]/g, ""));
}

/** Wrap $XXX / $X,XXX.XX matches in tappable spans. */
export function renderWithTapToTrace(text, onTap) {
  if (!text) return null;
  const parts = [];
  const regex = /\$\d{1,3}(?:,\d{3})*(?:\.\d{1,2})?|\$\d+(?:\.\d{1,2})?/g;
  let last = 0;
  let match;
  let key = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) parts.push(<span key={key++}>{text.slice(last, match.index)}</span>);
    const val = parseDollar(match[0]);
    if (val >= 3) {
      parts.push(
        <button
          key={key++}
          onClick={() => onTap(val)}
          data-testid={`trace-amt-${val}`}
          className="inline-flex items-baseline gap-0.5 rounded-md bg-emerald-100 text-emerald-800 px-1 py-0.5 -my-0.5 font-semibold hover:bg-emerald-200 transition-colors duration-150"
        >
          {match[0]}
        </button>
      );
    } else {
      parts.push(<span key={key++}>{match[0]}</span>);
    }
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(<span key={key++}>{text.slice(last)}</span>);
  return parts;
}

export default function TraceSheet({ amount, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (amount === null || amount === undefined) return;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/transactions/lookup?amount=${amount}`);
        setData(data);
      } catch { setData(null); }
      setLoading(false);
    })();
  }, [amount]);

  return (
    <Sheet open={amount !== null} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="bottom" className="rounded-t-3xl max-h-[80vh] overflow-y-auto" data-testid="trace-sheet">
        <SheetHeader className="text-left">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Trace this number</div>
          <SheetTitle className="font-manrope font-extrabold text-2xl tracking-tighter text-slate-900 tabular-nums">{fmt(amount)}</SheetTitle>
          {data && <div className="text-xs text-slate-500">Showing matches within {fmt(data.range.lo)}–{fmt(data.range.hi)}</div>}
        </SheetHeader>

        {loading ? (
          <div className="py-12 text-center text-slate-400 text-sm">Looking…</div>
        ) : !data || (data.transactions.length === 0 && data.matching_rollups.length === 0) ? (
          <div className="py-12 text-center text-slate-400 text-sm">
            <Receipt className="mx-auto mb-2" size={28} strokeWidth={1.25} />
            No transactions matched this amount.
          </div>
        ) : (
          <div className="mt-4 space-y-6 pb-8">
            {data.matching_rollups.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold mb-2">Matching category totals</div>
                <div className="space-y-2">
                  {data.matching_rollups.map((r, i) => (
                    <div key={i} className="rounded-2xl border border-emerald-200 bg-emerald-50 p-3 flex justify-between items-center">
                      <div>
                        <div className="font-medium text-slate-900 text-sm">{r.category}</div>
                        <div className="text-xs text-emerald-700">Total for {r.month}</div>
                      </div>
                      <div className="font-manrope font-bold text-emerald-700 tabular-nums">{fmtCents(r.total)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {data.transactions.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold mb-2">Matching transactions</div>
                <div className="space-y-2">
                  {data.transactions.map((t) => (
                    <div key={t.id} className="rounded-xl border border-slate-200 p-3 flex justify-between items-center" data-testid={`trace-tx-${t.id}`}>
                      <div className="min-w-0">
                        <div className="font-medium text-slate-900 text-sm truncate">{t.merchant}</div>
                        <div className="text-xs text-slate-500">{t.date} · {t.category}</div>
                      </div>
                      <div className={`font-manrope font-bold tabular-nums ${t.is_income ? "text-emerald-600" : "text-slate-900"}`}>
                        {t.is_income ? "+" : "-"}{fmtCents(t.amount)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
